import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Calendar, List, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';

/**
 * EmbedDemoPage - Demonstrates the Pro Embeddable Calendar widget
 * Shows both iframe and script embed methods with live preview
 */
export default function EmbedDemoPage() {
  const [copied, setCopied] = useState(null);
  const [city, setCity] = useState('');
  const [venue, setVenue] = useState('');
  const [view, setView] = useState('month');
  const [limit, setLimit] = useState('50');
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Build query params for preview
  const buildParams = () => {
    const params = new URLSearchParams();
    if (city) params.append('city', city);
    if (venue) params.append('venue', venue);
    params.append('view', view);
    params.append('limit', limit);
    return params.toString();
  };
  
  // Generate embed codes
  const scriptEmbed = `<script src="${backendUrl}/api/public/embed/calendar.js"${city ? ` data-city="${city}"` : ''}${venue ? ` data-venue-slug="${venue}"` : ''} data-view="${view}" data-limit="${limit}"></script>`;
  
  const iframeEmbed = `<iframe src="${backendUrl}/api/public/embed/calendar.html?${buildParams()}" style="width:100%;min-height:500px;border:none;border-radius:8px;" frameborder="0"></iframe>`;
  
  const handleCopy = async (type, code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/events" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Pro Embeddable Calendar</h1>
              <p className="text-sm text-slate-600">Add a professional events calendar to your website</p>
            </div>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Configuration Panel */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filter Options
                </CardTitle>
                <CardDescription>
                  Configure which events appear in your calendar widget
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All cities</SelectItem>
                      <SelectItem value="london">London</SelectItem>
                      <SelectItem value="birmingham">Birmingham</SelectItem>
                      <SelectItem value="manchester">Manchester</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="venue">Venue</Label>
                  <Select value={venue || "all"} onValueChange={(v) => setVenue(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All venues" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All venues</SelectItem>
                      <SelectItem value="excel-london">ExCeL London</SelectItem>
                      <SelectItem value="nec-birmingham">NEC Birmingham</SelectItem>
                      <SelectItem value="manchester-central">Manchester Central</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="view">Default View</Label>
                  <Select value={view} onValueChange={setView}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month">
                        <span className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Month View
                        </span>
                      </SelectItem>
                      <SelectItem value="list">
                        <span className="flex items-center gap-2">
                          <List className="h-4 w-4" />
                          List View
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="limit">Max Events</Label>
                  <Input 
                    id="limit"
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    min="1"
                    max="100"
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Embed Code */}
            <Card>
              <CardHeader>
                <CardTitle>Embed Code</CardTitle>
                <CardDescription>
                  Choose your preferred embed method
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="script">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="script">Script Tag</TabsTrigger>
                    <TabsTrigger value="iframe">iFrame</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="script" className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Recommended method. The script automatically creates the calendar widget.
                    </p>
                    <div className="relative">
                      <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-x-auto">
                        <code>{scriptEmbed}</code>
                      </pre>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="absolute top-2 right-2"
                        onClick={() => handleCopy('script', scriptEmbed)}
                      >
                        {copied === 'script' ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="iframe" className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Alternative method. Direct iframe embed with full control over sizing.
                    </p>
                    <div className="relative">
                      <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-x-auto">
                        <code>{iframeEmbed}</code>
                      </pre>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="absolute top-2 right-2"
                        onClick={() => handleCopy('iframe', iframeEmbed)}
                      >
                        {copied === 'iframe' ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            
            {/* Features */}
            <Card>
              <CardHeader>
                <CardTitle>Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Monthly calendar and list views
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Filter by city, venue, or organiser
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Click events to view full details
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Mobile-responsive design
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Google Analytics event tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Powered by Jigsaw branding
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* Live Preview */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Live Preview</CardTitle>
                <CardDescription>
                  See how your calendar will appear on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg border">
                  <iframe 
                    key={`${city}-${venue}-${view}-${limit}`}
                    src={`${backendUrl}/api/public/embed/calendar.html?${buildParams()}`}
                    style={{
                      width: '100%',
                      minHeight: '550px',
                      border: 'none',
                      borderRadius: '8px'
                    }}
                    frameBorder="0"
                    title="Calendar Preview"
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Additional data attributes */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Options</CardTitle>
                <CardDescription>
                  More data attributes for advanced configuration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Attribute</th>
                      <th className="text-left py-2">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600">
                    <tr className="border-b">
                      <td className="py-2 font-mono text-xs">data-city</td>
                      <td className="py-2">Filter by city slug (e.g., "london")</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono text-xs">data-venue-slug</td>
                      <td className="py-2">Filter by venue slug (e.g., "excel-london")</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono text-xs">data-organizer-id</td>
                      <td className="py-2">Filter by organiser email</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono text-xs">data-series-key</td>
                      <td className="py-2">Filter by event series</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono text-xs">data-view</td>
                      <td className="py-2">"month" or "list" (default: month)</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono text-xs">data-limit</td>
                      <td className="py-2">Max events to show (default: 50)</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
